import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api";
import { useAuthStore } from "../store";
import { LoginFormData, LoginResponse } from "../schemas";
import { queryKeys } from "./use-queries";
import { useToast } from "@/components/ui/toast";
import { useRouter } from "next/navigation";

// Login mutation
export const useLogin = () => {
  const queryClient = useQueryClient();
  const setAuth = useAuthStore((state) => state.setAuth);
  const { showToast } = useToast();
  const router = useRouter();

  return useMutation({
    mutationFn: async (credentials: LoginFormData): Promise<LoginResponse> => {
      try {
        const response = await api.post("/login", credentials);
        console.log(response, "response");
        
        if (response && response.data) {
          showToast("Login successful", "success");
          await fetch("/api/auth/set-token", {
            method: "POST",
            body: JSON.stringify({ token: response.data.access_token }),
          });

          router.push("/dashboard");
          return response.data;
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error: any) {
        console.error("Login mutation error:", error);
        
        // Handle network errors specifically
        if (error.name === 'NetworkError' || error.name === 'TimeoutError') {
          showToast(error.message, "error");
        } else if (error.response?.data?.message) {
          showToast(error.response.data.message, "error");
        } else {
          showToast("Something went wrong. Please try again.", "error");
        }
        
        throw error;
      }
    },
    onSuccess: (data) => {
      setAuth(data.admin, data.access_token);
      queryClient.invalidateQueries({ queryKey: queryKeys.admin });
    },
    onError: (error: any) => {
      console.error("Login error:", error);
    },
  });
};

// Update admin profile mutation
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { email?: string; role?: string }) => {
      const response = await api.put("/profile", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin });
    },
  });
};

// Create user mutation
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: any) => {
      const response = await api.post("/users", userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
};

// Update user mutation
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
};

// Delete user mutation
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
};

// Mark notification as read mutation
export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.put(`/notifications/${id}/read`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
};

// Logout mutation
export const useLogout = () => {
  const queryClient = useQueryClient();
  const logout = useAuthStore((state) => state.logout);
  const { showToast } = useToast();
  const router = useRouter();

  return useMutation({
    mutationFn: async () => {
      try {
        // Clear token from cookie
        await fetch("/api/auth/clear-token", {
          method: "POST",
        });

        // Clear Zustand state
        logout();

        // Clear all queries from cache
        queryClient.clear();

        showToast("Logged out successfully", "success");

        // Redirect to login page
        router.push("/login");

        return { success: true };
      } catch (error: any) {
        console.error("Logout error:", error);
        showToast("Error logging out", "error");
        throw error;
      }
    },
  });
};

// Approve or reject content creator request mutation
export const useApproveRejectRequest = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ userId, action, notes }: { userId: string; action: 'approved' | 'rejected'; notes?: string }) => {
      const response = await api.put('/content-creator-requests/approve', {
        userId,
        action,
        notes,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      const actionText = variables.action === 'approved' ? 'approved' : 'rejected';
      showToast(`Request ${actionText} successfully`, 'success');
      queryClient.invalidateQueries({ queryKey: queryKeys.contentCreatorRequests });
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
    },
    onError: (error: any) => {
      console.error('Approve/Reject request error:', error);
      showToast(
        error.response?.data?.message || 'Failed to process request',
        'error'
      );
    },
  });
};

// Approve or reject publish content mutation (single body payload)
export const useApproveRejectContent = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      contentId: string;
      contentType: string; // e.g., 'video' | 'post' | 'image'
      action: 'approved' | 'rejected';
      adminNotes?: string;
    }) => {
      const response = await api.put('/content/approve', payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      const actionText = variables.action === 'approved' ? 'approved' : 'rejected';
      showToast(`Content ${actionText} successfully`, 'success');
      queryClient.invalidateQueries({ queryKey: queryKeys.publishRequests });
    },
    onError: (error: any) => {
      console.error('Approve/Reject content error:', error);
      showToast(
        error.response?.data?.message || 'Failed to process content action',
        'error'
      );
    },
  });
};

export interface FrontroomVideoPayload {
  title: string;
  description: string;
  category: string;
  videoUrl: string;
  thumbnailUrl: string;
  duration: string;
  isPublished: boolean;
  order?: number;
  likes?: number;
  views?: number;
}

export const useCreateFrontroomVideo = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (payload: FrontroomVideoPayload) => {
      const response = await api.post("/frontroom/videos", payload);
      return response.data;
    },
    onSuccess: () => {
      showToast("Front room video created successfully", "success");
      queryClient.invalidateQueries({ queryKey: queryKeys.frontroomVideos });
    },
    onError: (error: any) => {
      console.error("Create front room video error:", error);
      showToast(error.response?.data?.message || "Failed to create video", "error");
    },
  });
};

export const useUpdateFrontroomVideo = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<FrontroomVideoPayload> }) => {
      const response = await api.patch(`/frontroom/videos/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      showToast("Front room video updated successfully", "success");
      queryClient.invalidateQueries({ queryKey: queryKeys.frontroomVideos });
    },
    onError: (error: any) => {
      console.error("Update front room video error:", error);
      showToast(error.response?.data?.message || "Failed to update video", "error");
    },
  });
};

export const useDeleteFrontroomVideo = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/frontroom/videos/${id}`);
    },
    onSuccess: () => {
      showToast("Front room video deleted successfully", "success");
      queryClient.invalidateQueries({ queryKey: queryKeys.frontroomVideos });
    },
    onError: (error: any) => {
      console.error("Delete front room video error:", error);
      showToast(error.response?.data?.message || "Failed to delete video", "error");
    },
  });
};

export interface ContentCreatorArticlePayload {
  title: string;
  category: string;
  shortDescription: string;
  description: string;
  coverImage: string;
  tags: string[];
  status: string;
}

export const useCreateContentCreatorArticle = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (payload: ContentCreatorArticlePayload) => {
      const response = await api.post("/content-creator/articles", payload);
      return response.data;
    },
    onSuccess: () => {
      showToast("Article created successfully", "success");
      queryClient.invalidateQueries({ queryKey: queryKeys.contentCreatorArticles });
    },
    onError: (error: any) => {
      console.error("Create article error:", error);
      showToast(error.response?.data?.message || "Failed to create article", "error");
    },
  });
};

export interface UpdateContentCreatorArticlePayload {
  title?: string;
  category?: string;
  shortDescription?: string;
  description?: string;
  coverImage?: string;
  tags?: string[];
}

export const useUpdateContentCreatorArticle = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateContentCreatorArticlePayload }) => {
      const response = await api.put(`/content-creator/articles/${id}`, payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      showToast("Article updated successfully", "success");
      queryClient.invalidateQueries({ queryKey: queryKeys.contentCreatorArticles });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.contentCreatorArticle, variables.id] });
    },
    onError: (error: any) => {
      console.error("Update article error:", error);
      showToast(error.response?.data?.message || "Failed to update article", "error");
    },
  });
};

export const useDeleteContentCreatorArticle = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/content-creator/articles/${id}`);
    },
    onSuccess: (_data, variables) => {
      showToast("Article deleted successfully", "success");
      queryClient.invalidateQueries({ queryKey: queryKeys.contentCreatorArticles });
      if (variables) {
        queryClient.invalidateQueries({ queryKey: [...queryKeys.contentCreatorArticle, variables] });
      }
    },
    onError: (error: any) => {
      console.error("Delete article error:", error);
      showToast(error.response?.data?.message || "Failed to delete article", "error");
    },
  });
};

export interface ContentCreatorPodcastPayload {
  title: string;
  category: string;
  description: string;
  thumbnail: string;
  videoUrl: string;
  tags: string[];
  duration: number;
}

export const useCreateContentCreatorPodcast = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (payload: ContentCreatorPodcastPayload) => {
      const response = await api.post("/content-creator/podcasts", payload);
      return response.data;
    },
    onSuccess: () => {
      showToast("Podcast created successfully", "success");
      queryClient.invalidateQueries({ queryKey: queryKeys.contentCreatorPodcasts });
    },
    onError: (error: any) => {
      console.error("Create podcast error:", error);
      showToast(error.response?.data?.message || "Failed to create podcast", "error");
    },
  });
};

export const useUpdateContentCreatorPodcast = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<ContentCreatorPodcastPayload> }) => {
      const response = await api.put(`/content-creator/podcasts/${id}`, payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      showToast("Podcast updated successfully", "success");
      queryClient.invalidateQueries({ queryKey: queryKeys.contentCreatorPodcasts });
      if (variables?.id) {
        queryClient.invalidateQueries({ queryKey: [...queryKeys.contentCreatorPodcast, variables.id] });
      }
    },
    onError: (error: any) => {
      console.error("Update podcast error:", error);
      showToast(error.response?.data?.message || "Failed to update podcast", "error");
    },
  });
};

export const useDeleteContentCreatorPodcast = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/content-creator/podcasts/${id}`);
    },
    onSuccess: (_data, variables) => {
      showToast("Podcast deleted successfully", "success");
      queryClient.invalidateQueries({ queryKey: queryKeys.contentCreatorPodcasts });
      if (variables) {
        queryClient.invalidateQueries({ queryKey: [...queryKeys.contentCreatorPodcast, variables] });
      }
    },
    onError: (error: any) => {
      console.error("Delete podcast error:", error);
      showToast(error.response?.data?.message || "Failed to delete podcast", "error");
    },
  });
};